import {
  applySnapshot,
  flow,
  Instance,
  SnapshotIn,
  types,
} from "mobx-state-tree";
import { AddAllowedDomainRPC } from "@pulse/pulse-rpcs/lib/email-cycle/addAllowedDomainRPC";
import { getAPIClient } from "../../networking/APIClient";
import { LeoRPCResult } from "@surya-digital/leo-ts-types";
import { useAddAllowedDomainRPCClientImpl } from "../rpcs/RPC";
import {
  BoundedNameModel,
  createBoundedNameModel,
} from "../../models/BoundedNameModel";
import { BoundedName } from "@pulse/pulse-rpcs/lib/types/boundedName";
import { BoundedNameValidationError } from "../../enums/BoundedNameValidationError";

const getInitialState = (): SnapshotIn<typeof CreateAllowedDomainStore> => ({
  domainName: createBoundedNameModel(),
  domain: createBoundedNameModel(),
  loadingCreateAllowedDomain: false,
});

export const CreateAllowedDomainStore = types
  .model("CreateAllowedDomainStore", {
    domainName: BoundedNameModel,
    domain: BoundedNameModel,
    loadingCreateAllowedDomain: types.boolean,
  })
  .views((store) => ({
    get disableSubmit(): boolean {
      const isEmptyData = !store.domainName.name || !store.domain.name;
      const hasValidationError =
        store.domainName.validationError ?? store.domain.validationError;

      return !!(isEmptyData || hasValidationError);
    },
  }))
  .actions((store) => ({
    createAllowedDomain: flow(function* () {
      try {
        store.loadingCreateAllowedDomain = true;

        const apiClient = getAPIClient(store);
        const request = new AddAllowedDomainRPC.Request(
          new BoundedName(store.domainName.name),
          new BoundedName(store.domain.name),
        );

        const result: LeoRPCResult<
          AddAllowedDomainRPC.Response,
          AddAllowedDomainRPC.Errors.Errors
        > = yield useAddAllowedDomainRPCClientImpl(apiClient).execute(request);

        if ("error" in result) {
          switch (result.error.code) {
            case AddAllowedDomainRPC.RPCError.DomainAlreadyExists:
              store.domain.validationError =
                BoundedNameValidationError.DuplicateName;
              break;
            case AddAllowedDomainRPC.RPCError.DomainNameAlreadyExists:
              store.domainName.validationError =
                BoundedNameValidationError.DuplicateName;
              break;
            default:
              result.error.code satisfies never;
          }
        }
      } finally {
        store.loadingCreateAllowedDomain = false;
      }
    }),
    resetStore: (): void => applySnapshot(store, getInitialState()),
  }));

export const createCreateAllowedDomainStore = (): Instance<
  typeof CreateAllowedDomainStore
> => CreateAllowedDomainStore.create(getInitialState());
